/**
 * Who and how long, on top of the call (web-vrs.html frame 2, approved
 * 2026-10-29): "Pat Rivera · 4:12". Pure rules, web and native.
 *
 * Who: only a name the room knows for sure. A direct call (p2p- room) names
 * the other person: their name in the room, else the name the call was placed
 * or answered with. A VRS call (vrs- room) names the hearing party by the
 * contact name the client called (client-profile.js persistActiveCall stores
 * it as targetName); a typed number has no name, so it shows the time alone.
 * A VRI or group call shows the time alone. The interpreter is never the
 * "who": their tile says "Interpreter #1042".
 *
 * How long: from when the call was answered. A ring this page saw end counts
 * from that moment; else the stored call's start (written when the call was
 * matched or answered, so a page reload keeps the count); else the moment
 * this page first saw the other person. Nothing while the call still rings.
 */
/** The name the web portal stores for a direct call it could not name. */
const UNNAMED_DIRECT_CALL = 'Direct call';
/**
 * The stored call when it is for this room, else undefined.
 *
 * @param {ICallClockStoredCall} storedCall - The stored call (vrs_active_call).
 * @param {string} roomName - The room this page is in.
 * @returns {ICallClockStoredCall | undefined}
 */
function forRoom(storedCall, roomName) {
    return storedCall && roomName && typeof storedCall.roomName === 'string'
        && storedCall.roomName.toLowerCase() === roomName.toLowerCase() ? storedCall : undefined;
}
/**
 * The hearing party of a VRS call by the contact name the client called, or
 * '' when the call has none (a typed number, another room's call, not a VRS
 * room). The phone captions name the Caller line with it too.
 *
 * @param {Object} input - The room facts.
 * @param {string} input.roomName - The room this page is in.
 * @param {ICallClockStoredCall} [input.storedCall] - The stored call.
 * @returns {string}
 */
export function calledPartyName({ roomName, storedCall }) {
    if (!roomName || !roomName.toLowerCase().startsWith('vrs-')) {
        return '';
    }
    const name = forRoom(storedCall, roomName)?.targetName;
    return typeof name === 'string' ? name.trim() : '';
}
/**
 * The other person's name for the top of the call, or '' for the time alone.
 *
 * @param {Object} input - The room facts.
 * @param {string} input.roomName - The room this page is in.
 * @param {string} [input.peerName] - The other person's name in the room, when they are in it.
 * @param {ICallClockStoredCall} [input.storedCall] - The stored call.
 * @returns {string}
 */
export function callPeerName({ peerName, roomName, storedCall }) {
    if (roomName && roomName.toLowerCase().startsWith('vrs-')) {
        return calledPartyName({ roomName, storedCall });
    }
    if (!roomName || !roomName.toLowerCase().startsWith('p2p-')) {
        return '';
    }
    const present = typeof peerName === 'string' ? peerName.trim() : '';
    if (present) {
        return present;
    }
    const stored = forRoom(storedCall, roomName)?.clientName;
    const name = typeof stored === 'string' ? stored.trim() : '';
    return name && name !== UNNAMED_DIRECT_CALL ? name : '';
}
/**
 * A stored start as epoch ms: a number, or an ISO time.
 *
 * @param {number|string} value - The stored startedAt.
 * @returns {number | undefined}
 */
function startOf(value) {
    const time = typeof value === 'number' ? value : typeof value === 'string' ? Date.parse(value) : NaN;
    return Number.isFinite(time) && time > 0 ? time : undefined;
}
/**
 * When the call's clock starts (epoch ms), or undefined for no clock yet.
 *
 * @param {Object} input - The room facts.
 * @param {number} [input.answeredAt] - When this page saw the call's ring end.
 * @param {number} [input.peerSeenAt] - When this page first saw the other person.
 * @param {string} input.roomName - The room this page is in.
 * @param {ICallClockStoredCall} [input.storedCall] - The stored call.
 * @returns {number | undefined}
 */
export function callClockStart({ answeredAt, peerSeenAt, roomName, storedCall }) {
    const stored = forRoom(storedCall, roomName);
    if (stored?.outgoingRing) {
        return undefined;
    }
    return answeredAt ?? startOf(stored?.startedAt) ?? peerSeenAt;
}
/**
 * The call's length as the top of the call shows it: "4:12", "1:02:03".
 *
 * @param {number} ms - How long the call has run.
 * @returns {string}
 */
export function formatCallClock(ms) {
    const total = Math.max(0, Math.floor((Number.isFinite(ms) ? ms : 0) / 1000));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = String(total % 60).padStart(2, '0');
    return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`;
}
/**
 * The words on top of the call: "Pat Rivera · 4:12", or "4:12" alone.
 *
 * @param {string} name - The other person's name, or ''.
 * @param {string} clock - The formatted length.
 * @returns {string}
 */
export function callTopText(name, clock) {
    return name ? `${name} · ${clock}` : clock;
}
